import React from "react";
import BasePage from "../BasePage";
import BGImage from "../../assets/img/Designer.webp";
import imgR from "../../assets/img/img-hero-r.webp";
import imgL from "../../assets/img/img-hero-l.webp";

const AboutUs: React.FC = () => {
  return (
    <BasePage
      title="About Us - UC Waves & NextGenWaves"
      description="Learn about the mission, team, and vision behind UC Waves and NextGenWaves."
      keywords="About Us, UC Waves, NextGenWaves, Team, Mission"
    >
      <main
        className="relative flex flex-col items-center justify-center pt-28 bg-cover bg-center min-h-screen px-6"
        style={{ backgroundImage: `url(${BGImage})` }}
      >
        {/* Header with images */}
        <div className="flex items-center gap-8 justify-between w-full max-w-5xl mb-10">
          <img
            src={imgL}
            alt="Hero decorative left"
            width={256} // largeur "md:w-40" en px
            height={256} // même valeur pour un carré
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
          <h1 className="text-6xl font-bold text-white text-center drop-shadow-lg">
            About Us
          </h1>

          <img
            src={imgR}
            alt="Hero decorative right"
            width={256}
            height={256}
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
        </div>

        {/* Content container */}
        <div className="bg-white bg-opacity-90 backdrop-blur-md  p-8 max-w-5xl text-gray-800 drop-shadow-lg mb-16">
          <h2 className="text-3xl font-semibold mb-4">
            UC Waves & NextGenWaves
          </h2>
          <p className="mb-6">
            At the heart of <strong>UcWaves</strong> is{" "}
            <strong>NextGenWaves</strong>, a seasoned collective of developers,
            architects, and builders committed to creating decentralized
            infrastructure for open, trustless, and expressive digital systems.
            With over 20 years of hands-on experience in deep technology, our
            team brings a rare breadth of expertise—from low-level embedded
            systems to high-scale backend architectures and cutting-edge
            blockchain frameworks.
          </p>
          <p className="mb-6">
            We are engineers, cryptographers, and protocol designers who believe
            in meaningful decentralization. Our mission is to build software
            that empowers users, respects privacy, and removes unnecessary
            intermediaries from human and institutional communication.
          </p>

          <h3 className="text-2xl font-semibold mb-3">
            Our Team DNA: From Silicon to Software
          </h3>
          <p className="mb-4">
            The members of NextGenWaves have contributed to and led projects
            across an unusually wide technological landscape. Our core
            competencies include:
          </p>
          <ul className="list-disc list-inside ml-6 space-y-2 mb-6">
            <li>
              Embedded systems and firmware design for consumer electronics and
              industrial platforms
            </li>
            <li>
              Signal processing, IoT, and low-level hardware abstraction layers
              (HALs)
            </li>
            <li>
              High-performance backend engineering with modern languages like
              Rust, Go, and C++
            </li>
            <li>
              Frontend frameworks and user-centric interfaces using React, Vue,
              and mobile technologies
            </li>
            <li>
              Web3 technologies, including smart contract development, token
              economics, and decentralized governance mechanisms
            </li>
            <li>
              Security-first development practices, from cryptographic
              primitives to secure APIs
            </li>
          </ul>
          <p className="mb-6">
            This multi-disciplinary foundation enables us to engineer complete
            systems—from on-chain logic to user-facing experiences—with
            precision, speed, and resilience.
          </p>

          <h3 className="text-2xl font-semibold mb-3">
            International Collaboration, Rooted in Canada
          </h3>
          <p className="mb-6">
            Although our organizational base is in Canada, our development team
            spans Austria, France, and beyond. We embrace a distributed,
            remote-first approach that prioritizes autonomy, talent, and
            contribution over geography.
          </p>
          <p className="mb-6">
            Our diversity in location and background translates into
            architectural decisions that are more robust, perspectives that are
            more globally aligned, and communication that is intentionally
            asynchronous and clear.
          </p>

          <h3 className="text-2xl font-semibold mb-3">What We’re Building</h3>
          <p className="mb-4">
            <strong>UcWaves</strong> is the first public release from the
            NextGenWaves studio: a decentralized opinion-sharing protocol with a
            uniquely expressive token architecture. It features:
          </p>
          <ul className="list-disc list-inside ml-6 space-y-2 mb-6">
            <li>
              A base token (<strong>NAU</strong>) representing platform
              engagement
            </li>
            <li>
              A binary burn-mint model creating <strong>NAUY</strong> and{" "}
              <strong>NAUN</strong> tokens for expression
            </li>
            <li>
              A staking system that incentivizes tokenized opinion-holding
            </li>
            <li>
              An eventual transition to decentralized governance through voting
              and token incentives
            </li>
          </ul>
          <p className="mb-6">
            Our core goal is to enable users to speak, signal, and stake around
            emerging topics and social signals in a way that’s transparent,
            trustless, and resistant to manipulation.
          </p>
          <p className="mb-6">
            UcWaves is not a financial platform—it’s a foundational experiment
            in tokenized discourse, on-chain commitment, and programmable
            governance. We believe crypto’s highest potential lies not in
            speculation, but in designing new interaction mechanisms for civil
            society, not just capital markets.
          </p>

          <h3 className="text-2xl font-semibold mb-3">Our Values</h3>
          <ul className="space-y-4 mb-6">
            <li>
              <span className="font-semibold">🔒 Privacy by Default</span>
              We do not collect, track, or monetize user data. The dApp is fully
              non-custodial and client-side. Users own their keys, their tokens,
              and their voice.
            </li>
            <li>
              <span className="font-semibold">⚙️ Technical Rigor</span>
              All smart contracts are written with an obsessive attention to
              detail, peer-reviewed internally, and structured for transparency.
              We believe protocol logic should be elegant, minimal, and
              provable.
            </li>
            <li>
              <span className="font-semibold">
                🌐 Accessibility without Compromise
              </span>
              We strive to balance usability with decentralization. Our frontend
              is intuitive and fast, while our backend is trustless and
              permissionless.
            </li>
            <li>
              <span className="font-semibold">🧱 Modularity</span>
              Everything we build is designed for composability—both with
              existing Ethereum-based infrastructure and potential future
              integrations in DeFi, identity, and voting.
            </li>
          </ul>

          <h3 className="text-2xl font-semibold mb-3">Where We’re Going</h3>
          <p className="mb-6">
            UcWaves is only the beginning. The tokenized opinion layer we’re
            building opens the door to:
          </p>
          <ul className="list-disc list-inside ml-6 space-y-2 mb-6">
            <li>
              Social consensus systems for DAOs and decentralized communities
            </li>
            <li>
              On-chain signaling tied to prediction markets or reputation graphs
            </li>
            <li>
              Programmable stances that can impact funding, proposals, or policy
            </li>
          </ul>
          <p className="mb-6">
            We believe that governance is communication, and communication
            deserves cryptographic certainty and economic skin-in-the-game.
          </p>

          <h3 className="text-2xl font-semibold mb-3">In Summary</h3>
          <p className="mb-4">
            NextGenWaves is a deeply technical team distributed across Canada,
            Austria, and France, with a track record of engineering systems from
            the chip to the cloud. With UcWaves, we’re turning that experience
            toward the problem of decentralized truth-signaling—where opinion
            becomes an economic asset and governance becomes composable.
          </p>
          <p className="mb-4">
            We don’t promise hype. We promise execution. We don’t chase trends.
            We build primitives. We don’t talk decentralization. We ship it.
          </p>
        </div>
      </main>
    </BasePage>
  );
};

export default AboutUs;
